// Stránka výsledků vyhledávání (issue #31, refaktor #74, vlastní index #67;
// screens/17-vyhledavani.md).
//
// Orchestrátor: rozhodne, ODKUD data přijdou, a poskládá stránku z builderů.
// Vlastní kreslení dělá `searchRender.js`, čtení nativního DOM `searchNative.js`,
// vlastní index `searchApi.js` + `searchApiModel.js`.
//
//   vlastní index:  searchApi.search()      → searchRender
//   fallback:       searchNative.readModel() → searchRender  (týž tvar modelu)
//
// Rozvržení stránky řeší CSS `order` nad `#content` (pages/_search.scss), ne
// přesuny v DOM. Od 2026-09-09 jdou PRODUKTY PRVNÍ (rozhodnutí Tomáše, vědomá
// odchylka od designu kategorie → značky → produkty → články → stránky).
//
// PROČ SE VYMĚŇUJE OBSAH nativního `#products` a nestaví vlastní kontejner:
// mřížka visí v `_search.scss` na ID selektoru `#products.bs-card-grid` (počty
// sloupců na 5 breakpointech). Druhý kontejner by musel id duplikovat.
//
// Fallback (koncepce §Fallback, Ú2): API má 1200 ms. Cokoli jiného než platná
// odpověď = necháme nativní výsledky být a jedeme cestu z #31. Commit našeho
// renderu je konečný — po něm už se nevrací, proto se rozhoduje PŘED zápisem
// do DOM, ne během něj.
//
// Idempotentní, styly visí na `html.bs-search-ready` — bez JS zůstane nativní
// (nestylovaný, ale funkční) výpis skupin.

import { isSearchPage, productBlock, readProducts, readModel } from './searchNative.js';
import {
  el,
  dressGroup,
  dressGroupHead,
  renderGroup,
  renderHeading,
  renderEmpty,
  updateTotals,
  updateProductCounts,
  countLabel,
  watchChips,
} from './searchRender.js';
import { getConfig } from './searchConfig.js';
import { search as apiSearch, recommend as apiRecommend, discardPrefetch } from './searchApi.js';
import { modelFromApi, didYouMean } from './searchApiModel.js';
import { cardNodes } from './productCardRender.js';
import { enhanceCards, observeCards } from './productCard.js';
import { currentQuery } from './searchSuggest.js';
import { iconHtml } from './icon.js';
import {
  trackSearch,
  trackSort,
  trackLoadMore,
  trackViewItemList,
  trackRecImpression,
  wireClicks,
  SERVICE_SEARCH,
  LIST_SEARCH,
} from './searchAnalytics.js';
import L from '../locale/cs/searchPage.js';

const READY = 'bs-search-ready';
const CLOAK = 'bs-scloak';
const PAGE_SIZE = 12;
const LABEL_MORE = L.more;

// Řada „Podobné skladem" (#284) — viz sekce níž.
const SIMILAR_MIN_SHARE = 0.5; // podíl vyprodaných v načtených kartách, od kterého se řada zkouší
const SIMILAR_MAX_CODES = 12; // kolik vyprodaných kódů se pošle jako zdroj
const SIMILAR_LIMIT = 8; // max karet v řadě
const SIMILAR_SLOT = 'search_similar_in_stock'; // `slot` v search_events + item_list_name v GA4
// hodnoty `strategy`, při kterých se kreslí
// (`alternative_in_stock` = nativní Alternativní produkty z indexu, #290)
const SIMILAR_STRATEGIES = ['category_in_stock', 'alternative_in_stock'];
const LABEL_SIMILAR = L.similar;
const LABEL_SIMILAR_SUB_CAT = L.similarSubCat;
const LABEL_SIMILAR_SUB = L.similarSub;
const LABEL_SIMILAR_PREV = L.similarPrev;
const LABEL_SIMILAR_NEXT = L.similarNext;
const LABEL_SORT_PREFIX = L.sortPrefix;
const LABEL_SORT_ARIA = L.sortAria;

// Hodnoty sloupce `engine` v `shared.search_events` (#68). Podíl obou měří,
// jak často vrstva degraduje na nativní Shoptet.
const ENGINE_API = 'typesense';
const ENGINE_NATIVE = 'shoptet_fallback';

// --------------------------------------------------------------- produkty
//
// Sekci produktů obléká obě cesty stejně; liší se jen to, KDO dosypává karty.
// Nativní cestou Shoptet (AJAX loadMore), API cestou náš `mountLoadMore`.

/** Obal sekce + hlavička s počtem. Vrací mřížku, nebo `null` (nic nenalezeno). */
function dressProductsSection(products) {
  const found = document.querySelector('#products-found');
  if (!found || found.dataset.bsSearchProducts) return productBlock();
  found.dataset.bsSearchProducts = '1';
  found.classList.add('bs-sgroup', 'bs-sgroup--products');

  const block = productBlock();
  dressGroupHead(found.querySelector('h4'), products.shown, !!block);

  if (!block) {
    // „Produkty nebyly nalezeny" — hláška zůstává jen když má skupiny kolem sebe
    const msg = found.querySelector('[data-testid="searchMsg"]');
    if (msg) msg.classList.add('bs-sempty-msg');
    return null;
  }

  const head = found.querySelector('h4');
  if (head) head.appendChild(el('span', 'bs-sprod__shown'));
  return block;
}

/**
 * Nativní cesta: nativní `.loadMore` zůstává funkční, jen ho oblečeme, a počty
 * po dosypání dorovnává observer.
 *
 * ⚠️ Observer čte NATIVNÍ stav (`total === null` → „N+"), takže se v API cestě
 * pouštět NESMÍ — přepsal by skutečné počty z indexu zpátky na odhad.
 */
function mountProducts() {
  const block = dressProductsSection(readProducts());
  if (!block) return;

  const controls = document.querySelector('.listingControls');
  const more = document.querySelector('.loadMore');
  if (more) {
    more.classList.add('bs-smore');
    const btn = more.querySelector('.loadMore__button');
    // šipka dolů = nativní tlačítko dostane jen ikonu, popisek zůstává z Shoptetu
    if (btn && !btn.querySelector('.bs-icon')) btn.insertAdjacentHTML('beforeend', iconHtml('arrow-right'));
    more.insertAdjacentElement('afterend', el('span', 'bs-sprod__shown'));
  } else if (controls) {
    controls.appendChild(el('span', 'bs-sprod__shown'));
  }

  // loadMore dosype karty AJAXem a ShoptetDOM* event u něj NEstřílí (CLAUDE.md)
  new MutationObserver(function () {
    updateProductCounts(readProducts());
  }).observe(block, { childList: true });
}

// --------------------------------------------------------------- montáž

function mountResults(host, model) {
  const head = renderHeading(model);
  // nativní `h2` („Výsledky vyhledávání „pila"") nahrazuje naše hlavička
  const native = host.querySelector('h2');
  if (native) {
    native.classList.add('bs-hidden-native');
    native.insertAdjacentElement('beforebegin', head);
  } else {
    host.insertAdjacentElement('afterbegin', head);
  }

  updateTotals(model);
  model.sections.forEach(function (section) {
    if (section.node) dressGroup(section.node, section);
  });
  mountProducts();
  updateProductCounts(readProducts());
}

function mountEmpty(host, query, suggestion) {
  if (host.querySelector('.bs-sempty')) return;
  // nativní hlášky („Výsledky vyhledávání …", „Produkty nebyly nalezeny")
  host.querySelectorAll('h2, .search-products-heading').forEach(function (node) {
    node.classList.add('bs-hidden-native');
  });
  host.insertAdjacentElement('afterbegin', renderEmpty(query, suggestion));
}

// --------------------------------------------------- montáž z vlastního indexu

/**
 * Vymění obsah nativní produktové mřížky za karty z indexu.
 * Nativní `#products` kontejner se ZACHOVÁ (drží ID selektor mřížky), mění se
 * jen jeho děti. `offset` = kolik karet už uživatel viděl (řazení 0, loadMore
 * průběžně) — GA4 `index` karet je globální, ne dávkový.
 */
function fillGrid(cards, query, offset) {
  let block = productBlock();
  if (!block && cards.length) {
    // Nativní hledání nic nenašlo — mřížka #products vůbec nevznikla a h4 je
    // přímo hláška „Produkty nebyly nalezeny". Index ale produkty má; bez
    // vlastní mřížky by hlavička hlásila počty nad prázdným tělem (#226).
    const found = document.querySelector('#products-found');
    if (found) {
      const msg = found.querySelector('h4[data-testid="searchMsg"]');
      if (msg) msg.textContent = L.productsHeading;
      block = el('div', 'products products-block');
      block.id = 'products';
      found.appendChild(block);
    }
  }
  if (!block) return null;
  block.textContent = '';
  block.appendChild(cardNodes(cards));
  enhanceCards(block);
  observeCards(block); // dosypané karty (loadMore) se obléknou samy
  // Delegace prokliků (a košíku) na kontejner, ne na kartu — loadMore dosypává další.
  wireClicks(block, { query: query, service: SERVICE_SEARCH, listId: LIST_SEARCH });
  trackViewItemList({ items: cards, query: query, engine: ENGINE_API, offset: offset || 0 });
  return block;
}

/** Postaví sekce z indexu do kontejneru skupin (nativní se schovají). */
function fillSections(host, model, query) {
  document.querySelectorAll('.search-results-group').forEach(function (node) {
    node.classList.add('bs-hidden-native');
  });

  let box = host.querySelector('.search-results-groups');
  if (!box) {
    box = el('div', 'search-results-groups');
    host.insertAdjacentElement('afterbegin', box);
  }
  model.sections.forEach(function (section) {
    box.appendChild(renderGroup(section));
  });
  // prokliky na kategorie / značky / články / stránky (GA4 `result_type`)
  wireClicks(box, { query: query, service: SERVICE_SEARCH });
}

// „Načíst dalších 12 produktů" (mock) — velikost další dávky; bez známého
// celku (nativní fallback) zůstává obecné „Načíst další".
function moreLabel(products) {
  if (products.total === null || !products.hasMore) return LABEL_MORE;
  const n = Math.min(PAGE_SIZE, products.total - products.shown);
  if (n < 1) return LABEL_MORE;
  return L.morePrefix + (n < 5 ? L.moreFew : L.moreMany) + countLabel(n, 'products');
}

/** Náš loadMore — nativní se schová (dosypával by nativní karty do naší mřížky). */
function mountLoadMore(host, state) {
  document.querySelectorAll('.loadMore').forEach(function (node) {
    node.classList.add('bs-hidden-native');
  });
  if (!state.model.products.hasMore) return;

  const wrap = el('div', 'bs-smore bs-smore--own');
  const btn = el('button', 'bs-smore__btn');
  btn.type = 'button';
  btn.appendChild(el('span', null, moreLabel(state.model.products)));
  btn.insertAdjacentHTML('beforeend', iconHtml('arrow-right'));
  wrap.appendChild(btn);

  // červený progress pruh (mock: 220×4, výplň = podíl zobrazených) — výplň
  // dorovnává updateProductCounts()
  const progress = el('div', 'bs-smore__progress');
  progress.appendChild(el('span'));
  wrap.appendChild(progress);

  const shown = el('span', 'bs-sprod__shown');
  wrap.appendChild(shown);

  btn.addEventListener('click', function () {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    loadMore(host, state, btn, wrap);
  });

  const found = document.querySelector('#products-found') || host;
  found.appendChild(wrap);
  updateProductCounts(state.model.products);
}

async function loadMore(host, state, btn, wrap) {
  const seq = state.gridSeq;
  const data = await apiSearch(state.query, { offset: state.offset, limit: PAGE_SIZE, order: state.order });
  if (seq !== state.gridSeq) {
    // mezitím přeřazeno — stará stránka do nové mřížky nepatří (B3-04); tlačítko
    // patří nové mřížce a jejímu mountLoadMore, ale to už proběhlo bez čekání na
    // tohle volání, takže ho odemkneme tady — jinak zůstane zamčené do reloadu.
    btn.disabled = false;
    btn.classList.remove('is-loading');
    return;
  }
  btn.disabled = false;
  btn.classList.remove('is-loading');
  if (!data) return; // ticho je lepší než hláška — tlačítko zůstane k dalšímu pokusu

  const block = productBlock();
  if (block) {
    block.appendChild(cardNodes(data.products || []));
    enhanceCards(block);
  }
  // Dosypané karty Shoptet nezná (jeho `view_item_list` běží při načtení
  // stránky), takže impresi hlásíme sami — jinak by v měření chyběly.
  trackLoadMore({ query: state.query, results: (data.products || []).length, engine: ENGINE_API });
  trackViewItemList({ items: data.products || [], query: state.query, engine: ENGINE_API, offset: state.offset });
  state.offset += (data.products || []).length;
  state.model = modelFromApi(data, state.query, state.offset, state.order);
  updateTotals(state.model);
  updateProductCounts(state.model.products);
  const label = btn.querySelector('span');
  if (label) label.textContent = moreLabel(state.model.products);
  if (!state.model.products.hasMore) wrap.removeChild(btn);
}

function mountApi(host, data, query, restore, order) {
  const model = modelFromApi(data, query, undefined, order);
  const state = { query: query, offset: (data.products || []).length, model: model, order: order, busy: false };
  wireRestore(state);

  if (!model.products.present && !model.sections.length) {
    mountEmpty(host, query, didYouMean(data));
    return;
  }

  const head = renderHeading(model);
  const native = host.querySelector('h2');
  if (native) {
    native.classList.add('bs-hidden-native');
    native.insertAdjacentElement('beforebegin', head);
  } else {
    host.insertAdjacentElement('afterbegin', head);
  }
  // čipy kategorií: řádek „co se vejde" + proklik (result_type `category_chip`)
  watchChips(head.querySelector('.bs-schips'));
  wireClicks(head, { query: query, service: SERVICE_SEARCH });

  fillSections(host, model, query);
  fillGrid(data.products || [], query, 0);
  const found = dressProductsSection(model.products); // bez nativního observeru
  updateTotals(model);

  // řazení sedí v řádku hlavičky vpravo (mock D01): desktop taby, mobil
  // a tablet jeden ovladač „Řadit: Shoda" (přepíná CSS podle šířky)
  const slot = head.querySelector('.bs-shead__sort');
  if (found && slot) {
    buildSortControls(state, function () {
      applySort(host, state, document.querySelector('.bs-smore--own'));
    }).forEach(function (node) {
      slot.appendChild(node);
    });
  }

  mountLoadMore(host, state);
  updateProductCounts(model.products);
  mountSimilar(host, state, data.products || []);

  // scroll se obnovuje až po vykreslení mřížky, jinak není kam skrolovat
  if (restore && restore.y) window.scrollTo(0, restore.y);
}

// ------------------------------------------------- řada „Podobné skladem"
//
// Issue #284: dotazy s největším objemem v LB exportech míří na sezónně
// vyprodané zboží („sloupový ventilátor", „klimatizace") — index i relevance
// jsou v pořádku, vyprodané jdou dolů (#226), ale zákazník vidí jen
// „Momentálně nedostupné" a odchází. Detail má panel alternativ (#227),
// stránka výsledků do té doby ne.
//
// Kdy: mezi NAČTENÝMI kartami (první stránka, po návratu zpět víc) je
// vyprodaných aspoň `SIMILAR_MIN_SHARE`. Vyprodané jsou řazené dolů, takže
// „první stránka je z půlky vyprodaná" = skladem už moc nezbývá.
// Odkud: `/recommend?kind=search` — produkty skladem: nejdřív nativní
// Alternativní produkty vyprodaných kusů (#290, `alternative_in_stock`), pak
// z listové kategorie řazené tržbou (`category_in_stock`), bez fallbacku.
// Kreslí se JEN při těchto dvou `strategy`: starší API (řetěz s globálními
// bestsellery) i prázdná kategorie (ventilátory v září) vrátí jinou hodnotu
// a řada nevznikne — prázdná kategorie je sortiment, ne UI. API i index bez
// vazeb vrací dál `category_in_stock`, takže build jde nasadit před API.
// Kde: od 2026-10-14 HNED ZA PRVNÍ ŘADOU karet (mock M03/D02; do té doby pod
// celou mřížkou, na iPhonu za 12 vyprodanými kartami). Sekce je položka mřížky
// `#products` přes všechny sloupce, v DOM za první stránkou karet; do řady ji
// zvedne CSS `order` (_search.scss `.bs-has-similar`, počet sloupců per šířka),
// takže loadMore dosypává dál na konec a mřížka se nestaví znovu. Karty =
// tytéž karty výpisu, pás = scroll-snap carousel se šipkami (vzor
// „Související" na detailu). Proklik měří vlastní delegace pásu, delegace
// mřížky ho přeskočí (searchAnalytics `wireClicks`, vnořený kontejner).
// Přeřazení řadu přepočítá, loadMore ne (rozhodnutí stojí na první stránce).

/** Vyprodané karty z načtené stránky; `null` = řada se nezkouší. */
function similarSources(cards) {
  const list = (cards || []).filter(function (card) {
    return card && card.sku;
  });
  if (!list.length) return null;
  const soldOut = list.filter(function (card) {
    return !card.in_stock;
  });
  if (!soldOut.length || soldOut.length / list.length < SIMILAR_MIN_SHARE) return null;
  return {
    codes: soldOut.slice(0, SIMILAR_MAX_CODES).map(function (card) {
      return card.sku;
    }),
    seen: list.map(function (card) {
      return card.sku;
    }),
    // jediná společná kategorie vyprodaných → podtitul ji pojmenuje
    category: soldOut.reduce(function (acc, card) {
      if (acc === false) return false;
      const name = typeof card.category === 'string' ? card.category : '';
      if (!name) return false;
      return acc === null || acc === name ? name : false;
    }, null),
  };
}

// Jediná živá řada na stránce (přeřazení ji staví znovu) — resize listener
// je proto jeden na modul, ne jeden na každou postavenou řadu.
let activeSimilar = null;
let similarResizeWired = false;

function removeSimilar(host) {
  activeSimilar = null;
  host.querySelectorAll('.bs-similar').forEach(function (node) {
    node.remove();
  });
  host.querySelectorAll('.bs-has-similar').forEach(function (node) {
    node.classList.remove('bs-has-similar');
  });
}

/** Šipky odrážejí kraje pásu (zhasnou obě, když se všechno vejde). */
function similarNav(row) {
  const track = row.track;
  if (!track || !row.nav) return;
  const max = track.scrollWidth - track.clientWidth;
  const set = function (btn, off) {
    btn.disabled = off;
    btn.classList.toggle('is-disabled', off);
  };
  set(row.nav[0], track.scrollLeft <= 4);
  set(row.nav[1], track.scrollLeft >= max - 4);
}

function renderSimilar(items, source, query, anchor) {
  const section = el('section', 'bs-sgroup bs-sgroup--similar bs-similar');
  section.dataset.bsGroup = '1';
  section.dataset.bsSimilar = String(items.length);

  const head = el('h4', null, LABEL_SIMILAR);
  section.appendChild(head);
  dressGroupHead(head, items.length);
  const sub = el(
    'span',
    'bs-similar__sub',
    source.category ? LABEL_SIMILAR_SUB_CAT + source.category : LABEL_SIMILAR_SUB,
  );
  const count = head.querySelector('.bs-sgroup__count');
  if (count) count.insertAdjacentElement('afterend', sub);
  else head.appendChild(sub);

  const track = el('div', 'bs-similar__track');
  const row = { track: track, nav: null };
  row.nav = [
    { dir: -1, label: LABEL_SIMILAR_PREV, glyph: 'arrow-left', cls: 'bs-similar__nav--prev' },
    { dir: 1, label: LABEL_SIMILAR_NEXT, glyph: 'arrow-right', cls: 'bs-similar__nav--next' },
  ].map(function (spec) {
    const btn = el('button', 'bs-btn bs-btn--circle bs-similar__nav ' + spec.cls);
    btn.type = 'button';
    btn.setAttribute('aria-label', spec.label);
    btn.insertAdjacentHTML('beforeend', iconHtml(spec.glyph));
    btn.addEventListener('click', function () {
      const left = track.scrollLeft + spec.dir * Math.max(240, track.clientWidth * 0.85);
      try {
        track.scrollTo({ left: left, behavior: 'smooth' });
      } catch (e) {
        track.scrollLeft = left;
      }
    });
    head.appendChild(btn);
    return btn;
  });

  track.appendChild(cardNodes(items));
  section.appendChild(track);
  enhanceCards(track);
  wireClicks(track, { query: query, slot: SIMILAR_SLOT, anchor: anchor });
  track.addEventListener(
    'scroll',
    function () {
      similarNav(row);
    },
    { passive: true },
  );
  activeSimilar = row;
  if (!similarResizeWired) {
    similarResizeWired = true;
    window.addEventListener(
      'resize',
      function () {
        if (activeSimilar) similarNav(activeSimilar);
      },
      { passive: true },
    );
  }
  // šipky až po vložení do dokumentu (do té doby je clientWidth 0)
  requestAnimationFrame(function () {
    similarNav(row);
  });
  return section;
}

async function mountSimilar(host, state, cards) {
  const source = similarSources(cards);
  if (!source) return;
  // přeřazení uprostřed čekání → odpověď patří starým výsledkům, zahodí se
  const seq = (state.similarSeq = (state.similarSeq || 0) + 1);

  const data = await apiRecommend({ kind: 'search', codes: source.codes, seen: source.seen, limit: SIMILAR_LIMIT });
  if (seq !== state.similarSeq) return;
  if (!data || SIMILAR_STRATEGIES.indexOf(data.strategy) < 0) return;
  const items = (data.items || []).filter(function (card) {
    return card && card.sku && card.in_stock;
  });
  if (!items.length) return;
  const grid = productBlock();
  if (!grid) return;

  removeSimilar(host);
  grid.appendChild(renderSimilar(items, source, state.query, source.codes[0]));
  grid.classList.add('bs-has-similar');
  trackRecImpression({ slot: SIMILAR_SLOT, anchor: source.codes[0], results: items.length });
  trackViewItemList({ items: items, query: state.query, engine: ENGINE_API, listName: SIMILAR_SLOT });
}

// --------------------------------------------------------------- řazení
//
// Nativní `.listSorting` na výsledcích hledání NENÍ (Shoptet ho tu nerenderuje),
// takže `listingTools.sortOptions()` je nepoužitelný — čte právě jeho. Volby
// proto stavíme sami, vzhled sdílíme s výpisem kategorie (`.bs-sort-tabs` z #13).
//
// „Nejprodávanější" (`order=bestseller`) řadí podle sezónního skóre
// `revenue_seasonal` (od 2026-09-21, dřív `revenue_90d`). Volba tu
// do 2026-08-09 SCHVÁLNĚ nebyla: pole bylo bez BQ enrichmentu u všech
// dokumentů 0 a řazení vracelo znak po znaku totéž co `relevance` (#92).
// Noční sync z reportingu (#94) prodejní signál doveze — ověřeno proti živému
// API: `/popular` hlásí `strategy: revenue_90d` (při nulách vrací
// `fallback_no_revenue_data`) a `order=bestseller` dává jiné pořadí než
// relevance. Kdyby sync někdy vypadl, degradace je tichá a přijatelná: řazení
// spadne zpátky na `_text_match`, tedy na relevanci.
//
// Pořadí voleb: bestseller hned za shodou — je to obchodně nejsilnější volba
// a v pásu chceme mít nejužitečnější věc po ruce.
const SORTS = [
  { key: 'relevance', label: L.sortLabels.relevance },
  { key: 'bestseller', label: L.sortLabels.bestseller },
  { key: 'price_asc', label: L.sortLabels.price_asc },
  { key: 'price_desc', label: L.sortLabels.price_desc },
  { key: 'newest', label: L.sortLabels.newest },
];

/** Řazení z URL — Shoptet neznámý parametr ignoruje, takže si ho můžeme nést. */
function orderFromUrl() {
  try {
    const value = new URL(window.location.href).searchParams.get('order');
    return SORTS.some((s) => s.key === value) ? value : 'relevance';
  } catch (e) {
    return 'relevance';
  }
}

function writeOrderToUrl(order) {
  try {
    const url = new URL(window.location.href);
    if (order === 'relevance') url.searchParams.delete('order');
    else url.searchParams.set('order', order);
    window.history.replaceState(null, '', url.toString());
  } catch (e) {
    /* starý prohlížeč bez History API */
  }
}

/**
 * Řazení: taby (`.bs-toolbar--search .bs-sort-tab`, desktop) a jeden ovladač
 * „Řadit: Shoda" (`.bs-ssort`, mobil a tablet, mock M01) nad TÝMŽ stavem.
 * Ovladač je pilulka s průhledným nativním `<select>` přes celou plochu:
 * na iPhonu otevře systémový výběr, klávesnice i čtečky ho znají. Select má
 * 16 px, jinak by Safari při fokusu zoomoval stránku.
 */
function buildSortControls(state, onChange) {
  const bar = el('div', 'bs-toolbar bs-toolbar--search');
  const tabs = el('div', 'bs-sort-tabs');
  const pick = el('label', 'bs-ssort');
  const text = el('span', 'bs-ssort__text');
  const select = el('select', 'bs-ssort__select');
  select.setAttribute('aria-label', LABEL_SORT_ARIA);

  function sync() {
    tabs.querySelectorAll('.bs-sort-tab').forEach(function (node) {
      node.classList.toggle('is-active', node.dataset.bsOrder === state.order);
    });
    select.value = state.order;
    const current = SORTS.filter((s) => s.key === state.order)[0] || SORTS[0];
    text.textContent = LABEL_SORT_PREFIX + current.label;
  }

  function choose(key) {
    if (state.order === key || state.busy) {
      sync();
      return;
    }
    state.order = key;
    writeOrderToUrl(key);
    sync();
    onChange();
  }

  SORTS.forEach(function (sort) {
    const tab = el('button', 'bs-sort-tab');
    tab.type = 'button';
    tab.textContent = sort.label;
    tab.dataset.bsOrder = sort.key;
    tab.addEventListener('click', function () {
      choose(sort.key);
    });
    tabs.appendChild(tab);
    const option = el('option', null, sort.label);
    option.value = sort.key;
    select.appendChild(option);
  });
  select.addEventListener('change', function () {
    choose(select.value);
  });

  bar.appendChild(tabs);
  pick.appendChild(text);
  pick.insertAdjacentHTML('beforeend', iconHtml('chevron-down', 'bs-ssort__chev'));
  pick.appendChild(select);
  sync();
  return [bar, pick];
}

/** Přeřazení = nový dotaz od nuly; mřížka se vymění, sekce zůstávají. */
async function applySort(host, state, wrap) {
  state.busy = true;
  state.gridSeq = (state.gridSeq || 0) + 1;
  const grid = productBlock();
  if (grid) grid.classList.add('is-busy');
  trackSort(state.order, state.query);

  const data = await apiSearch(state.query, { limit: PAGE_SIZE, order: state.order });
  state.busy = false;
  if (grid) grid.classList.remove('is-busy');
  if (!data) return; // beze změny — uživateli zůstane, co viděl

  state.offset = (data.products || []).length;
  state.model = modelFromApi(data, state.query, state.offset, state.order);
  fillGrid(data.products || [], state.query, 0);
  updateTotals(state.model);
  updateProductCounts(state.model.products);

  // tlačítko „Načíst další" musí odpovídat novému řazení i počtu
  if (wrap && wrap.parentNode) wrap.parentNode.removeChild(wrap);
  mountLoadMore(host, state);
  // řada „Podobné skladem" stojí na první stránce — s novým řazením znovu
  removeSimilar(host);
  state.similarSeq = (state.similarSeq || 0) + 1;
  mountSimilar(host, state, data.products || []);
}

// ------------------------------------------------- návrat tlačítkem zpět
//
// Uživatel dojede na 48 karet, klikne na produkt a vrátí se — bez tohohle by
// začínal znovu na 12 a na začátku stránky. Ukládá se JEN dotaz, počet karet
// a scroll (ne odpovědi — ty má LRU cache v `searchApi.js` a hlavně by se
// v sessionStorage rychle rozešly s katalogem).
//
// bfcache nás nezajímá: když prohlížeč stránku obnoví z něj, DOM je celý
// i se scrollem a `initSearchPage` se vůbec nespustí (guard na `.bs-shead`).

const RESTORE_KEY = 'bs-search-restore';

function saveRestore(state) {
  try {
    sessionStorage.setItem(
      RESTORE_KEY,
      JSON.stringify({ q: state.query, offset: state.offset, y: window.scrollY }),
    );
  } catch (e) {
    /* private mode */
  }
}

/** Uložený stav pro tentýž dotaz, nebo `null`. Čte se jednorázově. */
function takeRestore(query) {
  try {
    const raw = sessionStorage.getItem(RESTORE_KEY);
    sessionStorage.removeItem(RESTORE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return saved && saved.q === query && saved.offset > PAGE_SIZE ? saved : null;
  } catch (e) {
    return null;
  }
}

function wireRestore(state) {
  // `pagehide` chytí i odchod na iOS, kde `beforeunload` nefiruje
  window.addEventListener('pagehide', function () {
    saveRestore(state);
  });
}

// --------------------------------------------------------------- init

function finish() {
  document.documentElement.classList.add(READY);
  document.documentElement.classList.remove(CLOAK);
}

export function initSearchPage() {
  if (!isSearchPage()) return;
  const host = document.querySelector('#content');
  if (!host) return;
  if (host.querySelector('.bs-shead, .bs-sempty')) return; // idempotence

  const query = currentQuery();
  const cfg = getConfig();

  // Ú0 — vrstva se vůbec nespustí (guardy v searchConfig.js). Nula requestů
  // (prefetch z hlavičky, pokud vystřelil, se zruší).
  if (!cfg || !query) {
    discardPrefetch();
    mountNative(host);
    finish();
    return;
  }

  // Návrat z detailu: vyžádáme rovnou tolik karet, kolik jich uživatel měl
  // načtených, ať nezačíná znovu od dvanácti.
  const restore = takeRestore(query);
  const limit = restore ? Math.min(restore.offset, PAGE_SIZE * 8) : PAGE_SIZE;
  const order = orderFromUrl();
  const started = Date.now();
  // důvod neúspěchu (`timeout | http_<status> | net | json | invalid`) — jde do
  // analytiky s fallback událostí, jinak by z dat nešlo poznat, proč se degradovalo
  const diag = {};

  apiSearch(query, { limit: limit, order: order, diag: diag }).then(
    function (data) {
      // Rozhoduje se PŘED prvním zápisem do DOM — po commitu není návratu.
      if (data) {
        // `took` měříme z pohledu uživatele (síť + server), ne jen server time.
        // Display jde PŘED montáží, ať v GA4 předchází `view_item_list` mřížky.
        trackSearch({ query: query, results: data.total, took: Date.now() - started, engine: ENGINE_API });
        mountApi(host, data, query, restore, order);
      } else {
        mountNative(host, query, diag, started, cfg);
      }
      finish();
    },
    function () {
      mountNative(host, query, diag, started, cfg);
      finish();
    },
  ).catch(function (err) {
    // výjimka v mountApi/mountNative (nečekaný tvar odpovědi): stránka nesmí
    // zůstat pod cloakem bez hlášky (code review 2026-09-09, B3-03)
    try {
      console.error('[bs] searchPage', err);
    } catch (e) { /* konzole není */ }
    finish();
  });
}

/**
 * Fallback cesta z #31 — nativní výsledky, jen nastylované.
 *
 * Měří se stejně jako API cesta, jen s `engine: shoptet_fallback`. Podíl obou
 * hodnot je jediné, z čeho pak jde poznat, jak často vlastní index degraduje
 * (#68) — bez něj by v datech vypadal fallback jako by se nic nedělo. `diag`
 * a `started` (jen po neúspěšném dotazu API) dodají důvod a dobu čekání, `cfg`
 * konfiguraci, pod kterou dotaz odešel — 5xx mezitím shodí breaker a bez ní by
 * se fallback s `reason=http_5xx` neodeslal (trackSearch `config`).
 */
function mountNative(host, query, diag, started, cfg) {
  const model = readModel();
  if (!model.products.shown && !model.sections.length) mountEmpty(host, model.query);
  else mountResults(host, model);
  trackSearch({
    query: query || model.query,
    results: model.products.total === null ? model.products.shown : model.products.total,
    engine: ENGINE_NATIVE,
    reason: diag ? diag.reason : undefined,
    took: started ? Date.now() - started : undefined,
    config: cfg,
  });
}
